import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../services/api';
import HistoryDetailModal from './HistoryDetailModal';
import HistoryEditModal from './HistoryEditModal';
import AppleCarousel from './AppleCarousel';
import ConfirmationModal from './ConfirmationModal';

function FoodHistoryManagement({ userId }) {
  const { t } = useTranslation();
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedHistory, setSelectedHistory] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingHistory, setEditingHistory] = useState(null);
  const [feedbackModal, setFeedbackModal] = useState({
    isOpen: false,
    title: '',
    message: '',
    type: 'error'
  });

  useEffect(() => {
    loadHistory();
  }, []);

  const loadHistory = async () => {
    setLoading(true);
    try {
      const data = await api.getAllHistory();
      setHistory(data);
    } catch (error) {
      console.error('Error loading history:', error);
      setFeedbackModal({
        isOpen: true,
        title: 'Load failed',
        message: 'Failed to load history',
        type: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleHistoryClick = (item) => {
    setSelectedHistory(item);
    setShowDetailModal(true);
  };

  const handleEdit = (item) => {
    setEditingHistory(item);
    setShowDetailModal(false);
    setShowEditModal(true);
  };

  const handleSave = async (formData) => {
    try {
      if (editingHistory) {
        await api.updateHistoryItem(editingHistory.id, {...formData, userId});
      } else {
        await api.createHistoryItem({...formData, userId});
      }
      setShowEditModal(false);
      setEditingHistory(null);
      loadHistory();
      setFeedbackModal({
        isOpen: true,
        title: 'Saved successfully',
        message: 'History item saved successfully.',
        type: 'success'
      });
    } catch (error) {
      console.error('Error saving history:', error);
      setFeedbackModal({
        isOpen: true,
        title: 'Save failed',
        message: 'Failed to save history',
        type: 'error'
      });
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.deleteHistoryItem(id);
      setShowDetailModal(false);
      setSelectedHistory(null);
      loadHistory();
      setFeedbackModal({
        isOpen: true,
        title: 'Deleted successfully',
        message: 'History item deleted successfully.',
        type: 'success'
      });
    } catch (error) {
      console.error('Error deleting history:', error);
      setFeedbackModal({
        isOpen: true,
        title: 'Delete failed',
        message: 'Failed to delete history',
        type: 'error'
      });
    }
  };

  const handleAddNew = () => {
    setEditingHistory(null);
    setShowEditModal(true);
  };

  const getStatusEmoji = (status) => {
    switch (status) {
      case 'SAFE': return '✅';
      case 'UNSAFE': return '❌';
      case 'CAUTION': return '⚠️';
      default: return '❓';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  if (loading) {
    return <div className="history-management">🔄 Loading history...</div>;
  }

  return (
    <div className="history-management">
      <div className="section-header">
        <h2>📜 {t('history.management.title', 'Food History Management')}</h2>
        <button className="btn-primary" onClick={handleAddNew}>
          ➕ Add History
        </button>
      </div>

      <div className="history-list">
        {history.length === 0 ? (
          <p className="empty-state">📭 No history found. Add your first history item!</p>
        ) : (
          <AppleCarousel>
            {history.map((item) => (
              <div 
                key={item.id} 
                className={`history-item-card ${item.safetyStatus?.toLowerCase()}`}
                onClick={() => handleHistoryClick(item)}
              >
                <div className="history-item-header">
                  <div>
                    <h3>{getStatusEmoji(item.safetyStatus)} {item.foodName}</h3>
                    <p className="history-meta">
                      🌍 {item.culture} | 👤 {item.userId} | 🕐 {formatDate(item.checkedAt)}
                    </p>
                  </div>
                </div>
                {item.message && (
                  <p className="history-message-preview">
                    {item.message.length > 100 ? item.message.substring(0, 100) + '...' : item.message}
                  </p>
                )}
                <div className="history-item-footer">
                  <span className="click-hint">👆 Click to view details</span>
                </div>
              </div>
            ))}
          </AppleCarousel>
        )}
      </div>

      <HistoryDetailModal
        isOpen={showDetailModal}
        onClose={() => {
          setShowDetailModal(false);
          setSelectedHistory(null);
        }}
        historyItem={selectedHistory}
        onEdit={handleEdit}
        onDelete={handleDelete}
      />

      <HistoryEditModal
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setEditingHistory(null);
        }}
        historyItem={editingHistory}
        onSave={handleSave}
      />

      <ConfirmationModal
        isOpen={feedbackModal.isOpen}
        onClose={() => setFeedbackModal({ isOpen: false, title: '', message: '', type: 'error' })}
        onConfirm={() => {}}
        title={feedbackModal.title}
        message={feedbackModal.message}
        type={feedbackModal.type}
        confirmText="OK"
        cancelText=""
      />
    </div>
  );
}

export default FoodHistoryManagement;

